import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { seoConfig } from "../data/seoConfig";
import { americanHumanoidRobotsPage as page } from "../data/americanHumanoidRobots";

const statusClasses = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  prototype: "border-cyan-200 bg-cyan-50 text-cyan-800",
  research: "border-blue-200 bg-blue-50 text-blue-800",
  darpa: "border-indigo-200 bg-indigo-50 text-indigo-800",
  historical: "border-neutral-300 bg-neutral-100 text-neutral-700",
  watchlist: "border-purple-200 bg-purple-50 text-purple-800"
};

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-4xl">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">{title}</h2>
      {description && <p className="mt-3 text-base leading-8 text-neutral-600">{description}</p>}
    </div>
  );
}

function SourceButton({ href, children = "Open source" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="focus-ring inline-flex items-center justify-center rounded-full border border-neutral-300 bg-white px-3 py-2 text-xs font-black text-neutral-900 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
    >
      {children}
    </a>
  );
}

function StatusBadge({ status, tone }) {
  return (
    <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-black ${statusClasses[tone] || statusClasses.research}`}>
      {status}
    </span>
  );
}

function UsaHumanoidVisual() {
  const points = [
    { label: "CA", city: "Tesla + Figure", x: "16%", y: "52%" },
    { label: "OR", city: "Agility", x: "17%", y: "28%" },
    { label: "TX", city: "Apollo + NASA", x: "48%", y: "72%" },
    { label: "MA", city: "Atlas", x: "82%", y: "32%" },
    { label: "FL", city: "IHMC", x: "72%", y: "82%" },
    { label: "VA", city: "THOR + CHARLI", x: "75%", y: "54%" }
  ];

  return (
    <div className="relative min-h-[18rem] overflow-hidden rounded-[2rem] border border-white/80 bg-white/10 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.16)] backdrop-blur">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_22%_36%,rgba(59,130,246,0.42),transparent_8rem),radial-gradient(circle_at_66%_70%,rgba(239,68,68,0.32),transparent_9rem),radial-gradient(circle_at_50%_48%,rgba(255,255,255,0.34),transparent_10rem)]" />
      <div className="relative h-[18rem] rounded-[1.5rem] border border-white/30 bg-slate-950/35">
        <div className="absolute left-[10%] top-[18%] h-[11rem] w-[18rem] rounded-[55%_45%_52%_48%] border border-white/35 bg-white/10" />
        <div className="absolute left-[37%] top-[26%] h-[9rem] w-[17rem] rounded-[45%_55%_48%_52%] border border-white/35 bg-white/10" />
        <div className="absolute left-[61%] top-[24%] h-[10rem] w-[12rem] rounded-[48%_52%_62%_38%] border border-white/35 bg-white/10" />
        {points.map((point) => (
          <div key={point.label} className="absolute" style={{ left: point.x, top: point.y }}>
            <span className="absolute -left-2 -top-2 h-4 w-4 rounded-full border-2 border-white bg-blue-400 shadow-[0_0_0_8px_rgba(59,130,246,0.22)]" />
            <span className="ml-4 inline-flex rounded-full bg-white px-3 py-1 text-xs font-black text-slate-950 shadow-lg">{point.label}</span>
            <span className="ml-2 hidden rounded-full bg-slate-950/80 px-3 py-1 text-[11px] font-bold text-white shadow-lg sm:inline-flex">{point.city}</span>
          </div>
        ))}
      </div>
      <p className="relative mt-4 text-sm font-semibold leading-6 text-white/80">
        California, Oregon, Texas, Massachusetts, Florida and Virginia anchor the page because the strongest public evidence points to companies, NASA work and research labs in those states.
      </p>
    </div>
  );
}

function RobotComparisonTable({ robots }) {
  const headers = ["Robot", "Company / Lab", "State / City", "Status", "Main Use", "Mobility", "Key Hardware", "Year", "Source"];

  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
      <div className="max-w-full overflow-x-auto overscroll-x-contain" role="region" aria-label="American humanoid robot comparison table" tabIndex={0}>
        <table className="min-w-[1280px] w-full border-collapse text-left text-sm">
          <caption className="sr-only">Comparison of American humanoid robots by company, location, status, use, mobility, hardware, year and source.</caption>
          <thead className="bg-slate-950 text-white">
            <tr>
              {headers.map((header) => (
                <th key={header} scope="col" className="px-4 py-4 text-[11px] font-black uppercase tracking-[0.12em]">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {robots.map((robot, index) => (
              <tr key={robot.id} className={index % 2 === 0 ? "bg-white" : "bg-neutral-50"}>
                <th scope="row" className="px-4 py-4 align-top text-sm font-black text-neutral-950">{robot.name}</th>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.company}</td>
                <td className="px-4 py-4 align-top font-semibold text-neutral-800">🇺🇸 {robot.city}</td>
                <td className="px-4 py-4 align-top"><StatusBadge status={robot.status} tone={robot.statusTone} /></td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mainUse}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mobility}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.keyHardware}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.yearIntroduced}</td>
                <td className="px-4 py-4 align-top"><SourceButton href={robot.sourceUrl}>Source</SourceButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RobotCard({ robot }) {
  return (
    <article id={robot.id} className="soft-card scroll-mt-32 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-500">{robot.company}</p>
          <h3 className="mt-2 text-xl font-black tracking-tight text-neutral-950">{robot.name}</h3>
          <p className="mt-1 text-sm font-bold text-neutral-600">🇺🇸 {robot.city}</p>
        </div>
        <StatusBadge status={robot.status} tone={robot.statusTone} />
      </div>

      <div className="mt-5 grid gap-4 text-sm leading-6 text-neutral-700">
        <p><strong className="text-neutral-950">Overview</strong> {robot.overview}</p>
        <p><strong className="text-neutral-950">Technical notes</strong> {robot.technical}</p>
        <p><strong className="text-neutral-950">Mobility</strong> {robot.mobility}</p>
        <p><strong className="text-neutral-950">Autonomy level</strong> {robot.autonomy}</p>
        <p><strong className="text-neutral-950">Why it matters</strong> {robot.whyItMatters}</p>
        <p><strong className="text-neutral-950">Limits and current status</strong> {robot.limitations}</p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2 border-t border-neutral-200 pt-4">
        <span className="rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-black text-neutral-700">{robot.yearIntroduced}</span>
        <span className="rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-black text-neutral-700">{robot.state}</span>
        <SourceButton href={robot.sourceUrl}>{robot.sourceLabel}</SourceButton>
      </div>
    </article>
  );
}

function CardGrid({ items }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => {
        const heading = item.label || item.title || item.place || item.region || item.name;
        const anchorId = item.name ? item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : undefined;
        return (
        <article id={anchorId} key={heading} className="scroll-mt-32 rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_14px_45px_rgba(15,23,42,0.04)]">
          <h3 className="text-lg font-black tracking-tight text-neutral-950">{item.label || item.title || item.place || item.region || item.name}</h3>
          {item.value && <p className="mt-2 text-3xl font-black text-blue-700">{item.value}</p>}
          {item.robots && <p className="mt-2 text-sm font-black text-blue-700">{item.robots}</p>}
          {item.strength && <p className="mt-3 text-sm leading-6 text-neutral-700"><strong className="text-neutral-950">Strength</strong> {item.strength}</p>}
          {item.caution && <p className="mt-2 text-sm leading-6 text-neutral-700"><strong className="text-neutral-950">Caution</strong> {item.caution}</p>}
          {(item.detail || item.note || item.useCase) && <p className="mt-3 text-sm leading-6 text-neutral-600">{item.detail || item.note || item.useCase}</p>}
          <div className="mt-4 flex flex-wrap gap-2">
            {item.sourceUrl && <SourceButton href={item.sourceUrl}>Source</SourceButton>}
            {item.internalLink && (
              <Link to={item.internalLink.to} className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-800 transition hover:border-blue-400 hover:text-neutral-950">
                {item.internalLink.label}
              </Link>
            )}
          </div>
        </article>
        );
      })}
    </div>
  );
}

function Timeline({ items }) {
  return (
    <div className="relative grid gap-4 before:absolute before:left-4 before:top-2 before:h-[calc(100%-1rem)] before:w-px before:bg-neutral-200 sm:before:left-6">
      {items.map((item) => (
        <article key={`${item.year}-${item.title}`} className="relative grid gap-3 rounded-[1.5rem] border border-neutral-200 bg-white p-5 pl-12 shadow-[0_14px_45px_rgba(15,23,42,0.04)] sm:pl-16">
          <span className="absolute left-[0.6rem] top-6 h-3 w-3 rounded-full border-4 border-blue-100 bg-blue-700 sm:left-[1.1rem]" />
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-blue-50 px-3 py-1.5 text-xs font-black text-blue-700">{item.year}</span>
            <SourceButton href={item.sourceUrl}>{item.sourceLabel}</SourceButton>
          </div>
          <h3 className="text-lg font-black text-neutral-950">{item.title}</h3>
          <p className="text-sm leading-6 text-neutral-600">{item.detail}</p>
        </article>
      ))}
    </div>
  );
}

function ClassificationSection({ groups }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {groups.map((group) => (
        <article key={group.title} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_14px_45px_rgba(15,23,42,0.04)]">
          <h3 className="text-lg font-black tracking-tight text-neutral-950">{group.title}</h3>
          <ul className="mt-4 grid gap-2 text-sm font-semibold leading-6 text-neutral-700">
            {group.items.map((item) => (
              <li key={item} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />{item}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function CompanyTable({ companies }) {
  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
      <div className="max-w-full overflow-x-auto" role="region" aria-label="American humanoid robot companies" tabIndex={0}>
        <table className="min-w-[940px] w-full border-collapse text-left text-sm">
          <thead className="bg-neutral-950 text-white">
            <tr>
              {["Company / Lab", "Founded", "Headquarters", "Main robot", "Use case", "Status", "Source"].map((header) => (
                <th key={header} scope="col" className="px-4 py-4 text-[11px] font-black uppercase tracking-[0.12em]">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {companies.map((company, index) => (
              <tr key={company.name} className={index % 2 === 0 ? "bg-white" : "bg-neutral-50"}>
                <th scope="row" className="px-4 py-4 align-top font-black text-neutral-950">{company.name}</th>
                <td className="px-4 py-4 align-top text-neutral-700">{company.founded}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{company.headquarters}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{company.robot}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{company.useCase}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{company.status}</td>
                <td className="px-4 py-4 align-top"><SourceButton href={company.sourceUrl}>Source</SourceButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AmericanHumanoidRobots() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Humanoid Robots", to: "/robot-types/humanoid-robots" },
    { label: "United States", to: page.path }
  ];

  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.seoTitle,
      description: page.seoDescription,
      author: { "@type": "Organization", name: "TechniaHQRobot" },
      publisher: { "@type": "Organization", name: "TechniaHQRobot" },
      datePublished: "2026-07-08",
      dateModified: "2026-07-08",
      mainEntityOfPage: `${seoConfig.baseUrl}${page.path}`,
      about: page.robots.map((robot) => robot.name)
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "American humanoid robots",
      itemListElement: page.robots.map((robot, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: robot.name,
        url: `${seoConfig.baseUrl}${page.path}#${robot.id}`
      }))
    }
  ];

  return (
    <>
      <SEO title={page.seoTitle} description={page.seoDescription} path={page.path} type="article" schema={schema} />
      <Breadcrumbs items={breadcrumbs} />

      <section className="site-shell pb-8 pt-8 sm:pb-12 sm:pt-10 lg:pb-16">
        <div className="grid gap-6 rounded-[2rem] bg-slate-950 p-5 text-white shadow-[0_32px_90px_rgba(15,23,42,0.18)] sm:p-8 lg:grid-cols-[minmax(0,1fr)_28rem] lg:items-center lg:p-10">
          <div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em]">🇺🇸 USA robotics</span>
              <span className="rounded-full border border-blue-300/30 bg-blue-400/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-blue-100">Humanoid ecosystem</span>
            </div>
            <h1 className="mt-5 max-w-5xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">American Humanoid Robots</h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-200 sm:text-lg">
              US humanoid robotics includes AI labs, venture-backed startups, NASA platforms, DARPA-era research, warehouse pilots and industrial humanoid development.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-8 text-slate-300">
              Boston Dynamics pushed dynamic movement with Atlas. NASA developed Valkyrie and Robonaut for space and disaster-response research. Agility Robotics turned legged mobility into warehouse work with Digit. Figure AI, Apptronik and Tesla are building general-purpose humanoids for industrial environments. The important distinction is the difference between a polished demo, a supervised pilot, a research platform and a commercially repeatable deployment.
            </p>
            
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-300">
              <span>Updated {page.updatedAt}</span>
              <span>•</span>
              <span>{page.author}</span>
              <span>•</span>
              <span>{page.readingTime}</span>
            </div>
          </div>
          <UsaHumanoidVisual />
        </div>
      </section>

      <nav className="site-shell sticky top-16 z-30 pb-4" aria-label="United States humanoid page navigation">
        <div className="max-w-full overflow-x-auto rounded-full border border-neutral-200 bg-white/90 px-2 py-2 shadow-sm backdrop-blur">
          <div className="flex min-w-max gap-1">
            {[
              ["Overview", "#overview"],
              ["Robot List", "#robot-list"],
              ["Profiles", "#profiles"],
              ["Companies", "#companies"],
              ["Map", "#map"],
              ["Timeline", "#timeline"],
              ["Active vs Historical", "#classification"],
              ["Limitations", "#limitations"],
              ["Sources", "#sources"]
            ].map(([label, href]) => (
              <a key={href} href={href} className="focus-ring rounded-full px-4 py-2 text-xs font-black text-neutral-700 transition hover:bg-blue-50 hover:text-blue-700">
                {label}
              </a>
            ))}
          </div>
        </div>
      </nav>

      <div className="site-shell grid gap-14 pb-16">
        <ArticleAd placement="top" articleSlug="humanoid-robots-united-states" />
        <section id="overview" className="grid gap-6">
          <SectionHeader
            eyebrow="Overview"
            title="A robotics ecosystem built from factories, labs and government research"
            description="The US humanoid story is not one company. It is a stack locomotion research, NASA remote operation, DARPA challenge tasks, warehouse demand, AI labs, venture funding and industrial customers willing to run pilots."
          />
          <CardGrid items={page.summaryCards} />
        </section>

        <section id="robot-list" className="grid gap-6">
          <SectionHeader
            eyebrow="Comparison"
            title="American humanoid robot comparison table"
            description="The table stays short on purpose. Use the profile cards below for context, limits and source links. On mobile, the table scrolls horizontally instead of squeezing technical text into unreadable columns."
          />
          <RobotComparisonTable robots={page.robots} />
        </section>

        <section id="profiles" className="grid gap-6">
          <SectionHeader
            eyebrow="Robot profiles"
            title="Main American humanoid robots and research platforms"
            description="Each profile separates hardware, autonomy level, status and limitation. That keeps a factory pilot from being misread as a finished mass deployment."
          />
          <div className="grid gap-5 lg:grid-cols-2">
            {page.robots.map((robot) => <RobotCard key={robot.id} robot={robot} />)}
          </div>
        </section>

        <section id="map" className="grid gap-6">
          <SectionHeader
            eyebrow="Map"
            title="Humanoid Robotics Map of the United States"
            description="The locations identify company and research activity. They do not imply a customer deployment at each site."
          />
          <CardGrid items={page.mapLocations} />
        </section>

        <section id="timeline" className="grid gap-6">
          <SectionHeader
            eyebrow="Timeline"
            title="From space robotics to industrial humanoids"
            description="The timeline only uses years that can be tied to a source. Historical robots stay historical. Active platforms stay separate from old demos."
          />
          <Timeline items={page.timeline} />
        </section>

        <section id="classification" className="grid gap-6">
          <SectionHeader
            eyebrow="Classification"
            title="Active, research, historical and USA-linked robots"
            description="This section prevents the common mistake mixing bipedal humanoids, upper-body space robots, wheeled service robots and foreign-origin platforms in one flat list."
          />
          <ClassificationSection groups={page.classification} />
        </section>

        <section className="grid gap-6">
          <SectionHeader
            eyebrow="Why the US matters"
            title="Why the United States matters in humanoid robotics"
            description="The American advantage is not a slogan. It comes from compute, robotics software, capital, labs, customer pilots and decades of hard lessons from robots that fell, stalled or needed operator help."
          />
          <CardGrid items={page.whyUnitedStatesMatters} />
        </section>

        <section id="limitations" className="grid gap-6 rounded-[2rem] border border-amber-200 bg-amber-50 p-5 sm:p-7">
          <SectionHeader
            eyebrow="Reality check"
            title="What the United States has not solved yet"
            description="Deployment evidence needs to address the workload, human support and repeated performance."
          />
          <div className="grid gap-3 sm:grid-cols-2">
            {page.limitations.map((limit) => (
              <div key={limit} className="rounded-2xl border border-amber-200 bg-white px-4 py-3 text-sm font-semibold leading-6 text-neutral-800">
                {limit}
              </div>
            ))}
          </div>
        </section>

        <section id="companies" className="grid gap-6">
          <SectionHeader
            eyebrow="Companies"
            title="American humanoid robot companies and labs"
            description="These company cards include headquarters, main robot, use case and current status. Watchlist companies need official robot pages before they move into the main table."
          />
          <CompanyTable companies={page.companyCards} />
        </section>

        <section className="grid gap-6">
          <SectionHeader
            eyebrow="Global comparison"
            title="USA vs China vs Japan vs Europe"
            description="The page should not claim the United States is number one without a metric. This comparison explains the different strengths without turning the article into marketing."
          />
          <CardGrid items={page.globalComparison} />
        </section>

        <section className="grid gap-6">
          <SectionHeader
            eyebrow="Watchlist"
            title="USA-linked robots and robots that need careful labels"
            description="This section keeps the main table clean. A robot can be relevant to US readers without being a purely American bipedal humanoid."
          />
          <CardGrid items={page.watchlist} />
        </section>

        <section className="grid gap-6">
          <SectionHeader
            eyebrow="Related reading"
            title="Continue exploring humanoid robotics"
            description="Explore model profiles, robot software and industrial applications."
          />
          <div className="flex flex-wrap gap-2">
            {page.internalLinks.map((link) => (
              <Link key={link.to} to={link.to} className="focus-ring rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                {link.label}
              </Link>
            ))}
          </div>
        </section>

        <section id="sources" className="grid gap-6 rounded-[2rem] border border-neutral-200 bg-white p-5 sm:p-7">
          <SectionHeader
            eyebrow="Sources"
            title="Source policy for this page"
            description="Use official company, NASA, DARPA, university and lab pages first. Use Reuters, IEEE, ICRA, RSS papers and credible tech media only to support or clarify claims. Do not use social media alone as proof of deployment."
          />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[...page.robots.map((robot) => ({ label: robot.sourceLabel, url: robot.sourceUrl })), ...page.watchlist.map((item) => ({ label: item.name, url: item.sourceUrl }))]
              .filter((source, index, sources) => sources.findIndex((item) => item.url === source.url) === index)
              .map((source) => (
                <a key={source.url} href={source.url} target="_blank" rel="noreferrer noopener" className="focus-ring rounded-2xl border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm font-black text-neutral-800 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">
                  {source.label}
                </a>
              ))}
          </div>
        </section>

        <ArticleAd placement="bottom" articleSlug="humanoid-robots-united-states" />
      </div>

      <CTASection
        eyebrow="TechniaHQRobot robotics research"
        title="Explore companies and robot models"
        description="Compare model specifications and read the sources behind each company and deployment record."
        primaryLabel="Explore robot companies"
        primaryHref="/robotics-companies"
        secondaryLabel="Open humanoid robots"
        secondaryHref="/robot-types/humanoid-robots"
      />
    </>
  );
}
